/*!
 * effects.js — a sound's filter: and delay:, built one way for synth() and audio()
 * © 2026 Rob Canning — GPLv3
 *
 *     filter:{type:lp, freq:800, q:4}
 *     delay:{time:0.25, fb:0.6, mix:0.3, tone:1500, sat:0.5}
 *
 * The delay's loop is feedback.js's: fb to 1.5, a tone and a saturator in
 * it. (audio() had a copy of its own, fb held under 0.98 and no tone or
 * sat, so the same delay ran away on a synth and not on a file.) Times
 * arrive in seconds: the callers convert beats (beats.js).
 */

import { evalMaybeRandom } from "./patterns.js";
import { feedbackLoop, FB_MAX } from "./feedback.js";

/** A param as a number between lo and hi; not a number at all: lo. */
export const clamp = (x, lo, hi) => {
  x = Number(x);
  return Number.isFinite(x) ? Math.max(lo, Math.min(hi, x)) : lo;
};

/** A value as it is now (a number, or rand(...) rolled), or the default. */
const valueOf = (v, dflt) => {
  const r = evalMaybeRandom(v);
  return r != null && Number.isFinite(Number(r)) ? Number(r) : dflt;
};

const FILTER_TYPES = { lp: "lowpass", hp: "highpass", bp: "bandpass" };

/** A BiquadFilterNode from { type, freq | cutoff, q }, or null for none. */
export function buildFilter(ctx, cfg) {
  if (!cfg) return null;
  const type = String(cfg.type || cfg.mode || "lowpass").toLowerCase();
  const node = ctx.createBiquadFilter();
  node.type = FILTER_TYPES[type] ?? type;
  node.frequency.value = clamp(valueOf(cfg.freq ?? cfg.cutoff, 1200), 20, 20000);
  node.Q.value = clamp(valueOf(cfg.q, 0.707), 0.0001, 30);
  return node;
}

/**
 * A delay from { time, fb | feedback, mix, tone, sat }: { dry, wet, delay,
 * fbGain, loop }, or null for none. The caller wires dry and wet.
 */
export function buildDelay(ctx, cfg) {
  if (!cfg) return null;
  const mix = clamp(valueOf(cfg.mix, 0.2), 0, 1);
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  dry.gain.value = 1 - mix;
  wet.gain.value = mix;
  const delay = ctx.createDelay(5.0);
  delay.delayTime.value = clamp(valueOf(cfg.time, 0.25), 0, 5);
  // the loop through a tone and a saturator: fb past 1 runs away into a
  // held squeal, not into the ceiling
  const loop = feedbackLoop(ctx, delay, { fb: clamp(valueOf(cfg.fb ?? cfg.feedback, 0.25), 0, FB_MAX), tone: cfg.tone, sat: cfg.sat });
  return { dry, wet, delay, fbGain: loop.fbGain, loop };
}
